import type { LocaleMorpheus } from "@solvari/translations";

import {
  useI18nMainSite,
  useI18nMainSiteHomepage,
} from "@solvari/translations";
import { toAbsoluteUrl } from "@solvari/utils";

import { useHost, useHostLocale } from "~/shared/composables/useHost.ts";
import { ziggy } from "~/shared/libs/ziggy.ts";

/* eslint-disable @typescript-eslint/naming-convention */
type OrganizationSchema = {
  "@type": "Organization";
  address: {
    "@type": "PostalAddress";
    addressCountry: string;
    addressLocality: string;
    postalCode: string;
    streetAddress: string;
  };
  areaServed: {
    "@type": "Country";
    name: string;
  };
  contactPoint: {
    "@type": "ContactPoint";
    contactType: string;
    email: string;
    telephone: string;
  };
  description: string;
  foundingDate: string;
  logo: string;
  name: string;
  sameAs: string[];
  url: string;
};
/* eslint-enable @typescript-eslint/naming-convention */

const utrechtAddress = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  "@type": "PostalAddress",
  streetAddress: "Orteliuslaan 850",
  postalCode: "3528 BB",
  addressLocality: "Utrecht",
  addressCountry: "NL",
} as const;

const geraardsbergenAddress = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  "@type": "PostalAddress",
  streetAddress: "Adamstraat 20 Bus 2",
  postalCode: "9500",
  addressLocality: "Geraardsbergen",
  addressCountry: "BE",
} as const;

function useOrganizationSchema() {
  const { tr } = useI18nMainSiteHomepage();
  const { tr: trSite } = useI18nMainSite();

  const locale = useHostLocale();
  const origin = `https://${useHost()}`;
  const url = toAbsoluteUrl(ziggy("mainSite.homepage"), origin);
  const logo = toAbsoluteUrl("/shared/img/logo.png", origin);

  return () => {
    const sharedContactPoint = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      "@type": "ContactPoint",
      telephone: trSite("client.general.contact_phone_number"),
      email: "info@solvari.com",
    } as const;

    const sharedFields = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      "@type": "Organization",
      name: "Solvari",
      url,
      logo,
      foundingDate: "2009",
      description: tr("client.homepage.home_title"),
    } as const;

    const localeOrganizationData = {
      "nl-NL": () => ({
        ...sharedFields,
        address: utrechtAddress,
        contactPoint: {
          ...sharedContactPoint,
          contactType: "customer service",
        },
        areaServed: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          "@type": "Country",
          name: "Nederland",
        },
        sameAs: [
          trSite("client.general.homepage_footer_menu_facebook_url"),
          trSite("client.general.homepage_footer_menu_instagram_url"),
          trSite("client.general.homepage_footer_menu_youtube_url"),
          trSite("client.general.homepage_footer_menu_linkedin_url"),
        ],
      }),
      "be-NL": () => ({
        ...sharedFields,
        address: geraardsbergenAddress,
        contactPoint: {
          ...sharedContactPoint,
          contactType: "customer service",
        },
        areaServed: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          "@type": "Country",
          name: "België",
        },
        sameAs: [
          trSite("client.general.homepage_footer_menu_facebook_url"),
          trSite("client.general.homepage_footer_menu_instagram_url"),
          trSite("client.general.homepage_footer_menu_youtube_url"),
          trSite("client.general.homepage_footer_menu_linkedin_url"),
        ],
      }),
      "be-FR": () => ({
        ...sharedFields,
        address: geraardsbergenAddress,
        contactPoint: {
          ...sharedContactPoint,
          contactType: "service client",
        },
        areaServed: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          "@type": "Country",
          name: "Belgique",
        },
        sameAs: [
          trSite("client.general.homepage_footer_menu_facebook_url"),
          trSite("client.general.homepage_footer_menu_youtube_url"),
          trSite("client.general.homepage_footer_menu_linkedin_url"),
        ],
      }),
      "fr-FR": () => ({
        ...sharedFields,
        address: utrechtAddress,
        contactPoint: {
          ...sharedContactPoint,
          contactType: "service client",
        },
        areaServed: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          "@type": "Country",
          name: "France",
        },
        sameAs: [
          trSite("client.general.homepage_footer_menu_youtube_url"),
          trSite("client.general.homepage_footer_menu_linkedin_url"),
        ],
      }),
      "dk-DA": () => ({
        ...sharedFields,
        address: utrechtAddress,
        contactPoint: {
          ...sharedContactPoint,
          contactType: "kundeservice",
        },
        areaServed: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          "@type": "Country",
          name: "Danmark",
        },
        sameAs: [
          trSite("client.general.homepage_footer_menu_youtube_url"),
          trSite("client.general.homepage_footer_menu_linkedin_url"),
        ],
      }),
      "se-SV": () => ({
        ...sharedFields,
        address: utrechtAddress,
        contactPoint: {
          ...sharedContactPoint,
          contactType: "kundtjänst",
        },
        areaServed: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          "@type": "Country",
          name: "Sverige",
        },
        sameAs: [
          trSite("client.general.homepage_footer_menu_youtube_url"),
          trSite("client.general.homepage_footer_menu_linkedin_url"),
        ],
      }),
    } satisfies Record<LocaleMorpheus, () => OrganizationSchema>;

    return localeOrganizationData[locale]();
  };
}

export { useOrganizationSchema };
